import { useCallback, useMemo, useState } from 'react'
import { PauseCircle, Pause, Play } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { RelativeTime } from '@/components/DataDisplay'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import { fetchApps, suspendApp, APP_MOD_STR as S, type AppRow } from './appModApi'

/**
 * A5.24 — Ilovani to'xtatish (suspend). GET /admin/mini-apps (lane-A greenfield →
 * fail-soft bo'sh). To'xtatish/tiklash → POST /admin/mini-apps/:id/suspend
 * {suspended, reason}. To'xtatilgan ilova foydalanuvchida ochilmaydi (qaytariladi).
 */
export function AppSuspendScreen() {
  const { toast } = useToast()
  const [target, setTarget] = useState<{ row: AppRow; suspend: boolean } | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<AppRow>[]>(
    () => [
      {
        key: 'title',
        header: S.app,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.title}</span>
            <code className="text-xs text-accent">/{r.short_name}</code>
          </div>
        ),
      },
      {
        key: 'bot_username',
        header: S.bot,
        type: 'text',
        width: 160,
        render: (r) => (r.bot_username ? `@${r.bot_username}` : '—'),
      },
      {
        key: 'status',
        header: S.status,
        type: 'text',
        width: 140,
        render: (r) =>
          r.status === 'suspended' ? (
            <StatusBadge status="suspended" label={S.suspended} />
          ) : (
            <StatusBadge status="active" label={S.active} />
          ),
      },
      { key: 'installs', header: S.installs, type: 'number', width: 110, align: 'right' },
      {
        key: 'created_at',
        header: S.created,
        type: 'date',
        width: 150,
        render: (r) => <RelativeTime value={r.created_at} className="text-secondary" />,
      },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AppRow>[]>(
    () => [
      {
        id: 'suspend',
        label: S.suspend,
        icon: <Pause size={13} />,
        danger: true,
        visible: (r) => r.status !== 'suspended',
        onSelect: (r) => setTarget({ row: r, suspend: true }),
      },
      {
        id: 'resume',
        label: S.resume,
        icon: <Play size={13} />,
        visible: (r) => r.status === 'suspended',
        onSelect: (r) => setTarget({ row: r, suspend: false }),
      },
    ],
    [],
  )

  const fetcher = useCallback(fetchApps, [])

  return (
    <>
      <PageHeader title={S.suspendTitle} screenId="A5.24" />
      <DataTable<AppRow>
        key={reloadKey}
        screenId="A5.24"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <PauseCircle size={28} strokeWidth={1.5} />, title: S.appsEmpty }}
      />

      {target && (
        <DangerDialog
          open
          onClose={() => setTarget(null)}
          screenId="A5.24"
          level={2}
          title={target.suspend ? S.suspendConfirmTitle : S.resumeConfirmTitle}
          description={target.suspend ? S.suspendConfirmDesc : S.resumeConfirmDesc}
          confirmLabel={target.suspend ? S.suspend : S.resume}
          onConfirm={async ({ reason }) => {
            try {
              await suspendApp(target.row.id, target.suspend, reason)
              toast(S.suspendDone, { tone: 'success' })
              setTarget(null)
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
              throw e
            }
          }}
        />
      )}
    </>
  )
}
